import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Layout } from '../components/Layout';
import { FadeInUp, StaggerContainer, StaggerItem, SlideInLeft, SlideInRight } from '../components/AnimatedSection';
import { DollarSign, Clock, Cpu, Leaf, ExternalLink, Building, TrendingUp, Globe } from 'lucide-react';

const Construction = () => {
  const { language, t } = useLanguage();

  const benefits = [
    { key: 'cost', icon: DollarSign },
    { key: 'time', icon: Clock },
    { key: 'digital', icon: Cpu },
    { key: 'sustainable', icon: Leaf }
  ];

  // Global projects data
  const globalProjects = [
    {
      title: language === 'ro' ? 'Clădire de Birouri - Dubai, UAE' : 'Office Building - Dubai, UAE',
      desc: language === 'ro' 
        ? 'Prima clădire de birouri imprimată 3D din lume, construită în doar 17 zile cu 50% mai puține costuri de forță de muncă.'
        : 'World\'s first 3D printed office building, built in just 17 days with 50% lower labor costs.',
      image: 'https://images.unsplash.com/photo-1748145883131-9a31515082eb?w=600&q=80',
      stat: '17',
      statLabel: language === 'ro' ? 'Zile' : 'Days',
      link: 'https://www.weforum.org/agenda/2023/03/3d-printed-homes-construction/'
    },
    {
      title: language === 'ro' ? 'Proiect TECLA - Italia' : 'TECLA Project - Italy',
      desc: language === 'ro'
        ? 'Locuință eco-sustenabilă imprimată din materiale locale, demonstrând potențialul construcțiilor cu amprentă zero de carbon.'
        : 'Eco-sustainable dwelling printed from local materials, demonstrating the potential of zero-carbon footprint construction.',
      image: 'https://images.unsplash.com/photo-1668642411999-ee03413d7038?w=600&q=80',
      stat: '200',
      statLabel: language === 'ro' ? 'Ore' : 'Hours',
      link: 'https://www.archdaily.com/959538/tecla-3d-printed-habitat-mario-cucinella-architects-plus-wasp'
    },
    {
      title: language === 'ro' ? 'Comunitate 3D - Texas, SUA' : '3D Community - Texas, USA',
      desc: language === 'ro'
        ? 'ICON construiește prima comunitate de case imprimate 3D din America, cu locuințe de 150mp în sub 48 de ore.'
        : 'ICON is building America\'s first 3D printed home community, with 150sqm homes in under 48 hours.',
      image: 'https://images.unsplash.com/photo-1636884741166-6c1d4be12da8?w=600&q=80',
      stat: '48',
      statLabel: language === 'ro' ? 'Ore/Casă' : 'Hours/Home',
      link: 'https://www.iconbuild.com'
    },
    {
      title: language === 'ro' ? 'Clădiri Multi-Etaj - Germania' : 'Multi-Story Buildings - Germany',
      desc: language === 'ro'
        ? 'PERI Group a construit prima clădire rezidențială cu 3 etaje imprimată 3D din Europa, dovedind scalabilitatea tehnologiei.'
        : 'PERI Group built Europe\'s first 3-story 3D printed residential building, proving the technology\'s scalability.',
      image: 'https://images.unsplash.com/photo-1689097765695-b96cc414c3f4?w=600&q=80',
      stat: '3',
      statLabel: language === 'ro' ? 'Etaje' : 'Floors',
      link: 'https://www.peri.com/en/business-segments/3d-construction-printing.html'
    }
  ];

  // Market statistics for chart
  const marketGrowth = [
    { year: '2023', value: 15, label: '$15B' },
    { year: '2024', value: 20, label: '$20B' },
    { year: '2025', value: 28, label: '$28B' },
    { year: '2030', value: 80, label: '$80B' }
  ];

  const maxValue = Math.max(...marketGrowth.map(d => d.value));

  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 bg-white" data-testid="construction-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <span className="confidential-badge mb-6 inline-block">
              {t('construction.badge')}
            </span>
          </FadeInUp>
          
          <FadeInUp delay={0.1}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy mb-6" data-testid="construction-title">
              {t('construction.title')}
            </h1>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-lg md:text-xl text-[#0066CC] font-medium max-w-3xl">
              {t('construction.subtitle')}
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Visual Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <SlideInLeft>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1653164579768-ea97833b3b03?w=800&q=80"
                  alt="3D Printed Building Model"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute -bottom-6 -right-6 bg-navy text-white p-6 hidden md:block">
                  <p className="text-2xl font-oswald font-bold">24-72h</p>
                  <p className="text-[10px] uppercase tracking-wider mt-1 text-white/70">Ultra Fast Build</p>
                  <p className="text-[10px] uppercase tracking-wider text-white/50">Impossible Design</p>
                </div>
              </div>
            </SlideInLeft>
            
            <SlideInRight>
              <img
                src="https://images.unsplash.com/photo-1705435443661-927632c3c9cb?w=800&q=80"
                alt="Modern Construction Technology"
                className="w-full aspect-[4/3] object-cover"
              />
            </SlideInRight>
          </div>
        </div>
      </section>

      {/* Global Projects Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-6 h-6 text-[#0066CC]" />
              <h2 className="text-2xl md:text-3xl font-bold text-navy">
                {language === 'ro' ? 'Proiecte Globale Remarcabile' : 'Notable Global Projects'}
              </h2>
            </div>
            <p className="text-slate-600 mb-12 max-w-2xl">
              {language === 'ro' 
                ? 'Case, birouri și clădiri multi-etaj imprimate 3D din întreaga lume demonstrează că viitorul construcțiilor este deja aici.'
                : 'Houses, offices and multi-story buildings 3D printed around the world demonstrate that the future of construction is already here.'}
            </p>
          </FadeInUp>

          <StaggerContainer className="grid md:grid-cols-2 gap-6" staggerDelay={0.15}>
            {globalProjects.map((project, index) => (
              <StaggerItem key={index}>
                <div className="border border-slate-200 overflow-hidden hover:border-[#0066CC] transition-colors group">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-navy text-white px-3 py-2">
                      <p className="text-xl font-oswald font-bold">{project.stat}</p>
                      <p className="text-[10px] uppercase tracking-wider text-white/70">{project.statLabel}</p>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-semibold text-navy mb-2">{project.title}</h3>
                    <p className="text-sm text-slate-600 mb-4">{project.desc}</p>
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-navy hover:text-[#0066CC] transition-colors"
                    >
                      Learn More
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* News & Resources */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <h2 className="text-2xl md:text-3xl font-bold text-navy mb-8">
              {language === 'ro' ? 'Resurse și Știri' : 'Resources & News'}
            </h2>
          </FadeInUp>

          <div className="grid md:grid-cols-3 gap-6">
            <FadeInUp delay={0.1}>
              <a 
                href="https://constructive-voices.com/ro/7-case-frumoase-imprimate-3d/"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white border border-slate-200 p-6 hover:border-[#0066CC] transition-colors group"
              >
                <Building className="w-8 h-8 text-[#0066CC] mb-4" />
                <h3 className="font-semibold text-navy mb-2 group-hover:text-[#0066CC] transition-colors">
                  {language === 'ro' ? '7 Case Frumoase Imprimate 3D' : '7 Beautiful 3D Printed Houses'}
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  {language === 'ro' 
                    ? 'Galerie cu cele mai impresionante locuințe imprimate 3D din lume.'
                    : 'Gallery of the world\'s most impressive 3D printed homes.'}
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#0066CC] font-semibold uppercase tracking-wider">
                  constructive-voices.com <ExternalLink className="w-3 h-3" />
                </span>
              </a>
            </FadeInUp>

            <FadeInUp delay={0.2}>
              <a 
                href="https://www.iconbuild.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white border border-slate-200 p-6 hover:border-[#0066CC] transition-colors group"
              >
                <Building className="w-8 h-8 text-[#0066CC] mb-4" />
                <h3 className="font-semibold text-navy mb-2 group-hover:text-[#0066CC] transition-colors">
                  ICON - {language === 'ro' ? 'Lider în Construcții 3D' : '3D Construction Leader'}
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  {language === 'ro' 
                    ? 'Compania care construiește comunități întregi cu imprimante 3D în Texas.'
                    : 'The company building entire communities with 3D printers in Texas.'}
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#0066CC] font-semibold uppercase tracking-wider">
                  iconbuild.com <ExternalLink className="w-3 h-3" />
                </span>
              </a>
            </FadeInUp>

            <FadeInUp delay={0.3}>
              <a 
                href="https://cobod.com/projects/"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white border border-slate-200 p-6 hover:border-[#0066CC] transition-colors group"
              >
                <Building className="w-8 h-8 text-[#0066CC] mb-4" />
                <h3 className="font-semibold text-navy mb-2 group-hover:text-[#0066CC] transition-colors">
                  COBOD - {language === 'ro' ? 'Proiecte Globale' : 'Global Projects'}
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  {language === 'ro' 
                    ? 'Portofoliul complet de clădiri imprimate cu tehnologia BOD2.'
                    : 'Complete portfolio of buildings printed with BOD2 technology.'}
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#0066CC] font-semibold uppercase tracking-wider">
                  cobod.com <ExternalLink className="w-3 h-3" />
                </span>
              </a>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Market Growth Chart */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-6 h-6 text-[#0066CC]" />
              <h2 className="text-2xl md:text-3xl font-bold text-navy">
                {language === 'ro' ? 'Creșterea Pieței Construcțiilor 3D' : '3D Construction Market Growth'}
              </h2>
            </div>
            <p className="text-slate-600 mb-12 max-w-2xl">
              {language === 'ro' 
                ? 'Piața globală a construcțiilor 3D este proiectată să crească de la $15B în 2023 la $80B până în 2030.'
                : 'The global 3D construction market is projected to grow from $15B in 2023 to $80B by 2030.'}
            </p>
          </FadeInUp>

          <FadeInUp delay={0.2}>
            <div className="bg-slate-50 p-8 border border-slate-200">
              <div className="flex items-end justify-between gap-4 h-48">
                {marketGrowth.map((data, index) => (
                  <div key={data.year} className="flex-1 flex flex-col items-center">
                    <span className="text-sm font-semibold text-navy mb-2">{data.label}</span>
                    <div 
                      className="w-full bg-[#0066CC] transition-all duration-700"
                      style={{ 
                        height: `${(data.value / maxValue) * 100}%`,
                        minHeight: '20px'
                      }}
                    />
                    <span className="text-xs text-slate-500 mt-2">{data.year}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-4 text-center">
                {language === 'ro' ? 'Sursa: Rapoarte industrie globală 2024' : 'Source: Global Industry Reports 2024'}
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-slate-50" data-testid="construction-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <h2 className="text-2xl md:text-3xl font-bold text-navy mb-12">
              {t('construction.benefits.title')}
            </h2>
          </FadeInUp>

          <StaggerContainer className="grid md:grid-cols-2 gap-6" staggerDelay={0.1}>
            {benefits.map((benefit) => {
              const IconComponent = benefit.icon;
              return (
                <StaggerItem key={benefit.key}>
                  <div 
                    className="bg-white border border-slate-200 p-8 hover:border-[#0066CC] transition-colors group"
                    data-testid={`benefit-${benefit.key}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-slate-100 group-hover:bg-[#0066CC] transition-colors">
                        <IconComponent className="w-6 h-6 text-slate-600 group-hover:text-white transition-colors" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-navy mb-2">
                          {t(`construction.benefits.${benefit.key}.title`)}
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          {t(`construction.benefits.${benefit.key}.desc`)}
                        </p>
                      </div>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* Romania Potential */}
      <section className="py-20 bg-navy text-white" data-testid="construction-romania">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeInUp>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-6">
                  {t('construction.romania.title')}
                </h2>
                <p className="text-white/80 text-base md:text-lg leading-relaxed">
                  {t('construction.romania.text')}
                </p>
              </div>
            </FadeInUp>
            
            <FadeInUp delay={0.2}>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 p-6">
                  <p className="text-3xl font-oswald font-bold">40%</p>
                  <p className="text-xs uppercase tracking-wider mt-1 text-white/60">
                    {language === 'ro' ? 'Economie Costuri' : 'Cost Savings'}
                  </p>
                </div>
                <div className="bg-white/10 p-6">
                  <p className="text-3xl font-oswald font-bold">60%</p>
                  <p className="text-xs uppercase tracking-wider mt-1 text-white/60">
                    {language === 'ro' ? 'Mai Puține Deșeuri' : 'Less Waste'}
                  </p>
                </div>
                <div className="bg-white/10 p-6">
                  <p className="text-3xl font-oswald font-bold">10x</p>
                  <p className="text-xs uppercase tracking-wider mt-1 text-white/60">
                    {language === 'ro' ? 'Mai Rapid' : 'Faster Build'}
                  </p>
                </div>
                <div className="bg-white/10 p-6">
                  <p className="text-3xl font-oswald font-bold">100%</p>
                  <p className="text-xs uppercase tracking-wider mt-1 text-white/60">
                    {language === 'ro' ? 'Control Digital' : 'Digital Control'}
                  </p>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Construction;
